import { AxiosResponse } from "axios";
import api from "@services/axios/apiManager";

/**
 * 帳號／瓦斯帳戶 紀錄查詢三支 API（對齊 lpg-app-api `app-account-history-spec.md`）。
 * - 帳號付費紀錄：GET /app-api/customer/payment-records（customer 視角，過濾 placed_by=caller）
 * - 帳戶訂單紀錄：GET /app-api/platform-orders/account-history（需 X-Context）
 * - 帳戶付款紀錄+月結：GET /app-api/platform-orders/account-payments（需 X-Context）
 */

type ApiResult<T> = { state: "success"; data: T } | { state: "error"; error: unknown };

/* ──────────────── 帳號付費紀錄（payment-records） ──────────────── */

export interface PaymentRecordItem {
  platform_order_id: string;
  /** 人類可讀訂單編號（= order_list.order_id，例 "ADO_8805"，後端取自 platform_order_supplier_link.order_id）；尚未綁定 supplier / order_id 未產生時為 null */
  display_id: string | null;
  context_id: number;
  /** PlatformOrderStatusEnum：pending / waiting_for_pickup / delivering / completed / canceled / failed */
  order_status: string;
  /** 顯示名稱：context_name 或 fallback supplier_name */
  display_title: string;
  /** 副標：瓦斯行名 · 付款方式 label */
  display_subtitle: string | null;
  amount: number | null;
  payment_method_label: string | null;
  created_at: string;
  /** 實際付款時間（ISO 8601）。列表已由後端過濾為已付款訂單，理論上必有值。 */
  paid_at: string | null;
  /** 該訂單所屬 supplier 的 system_setting.hide_amount_on_pending；待確認且為 true 時前端隱藏金額。 */
  hide_amount_on_pending?: boolean;
}

export interface PaymentRecordsRes {
  items: PaymentRecordItem[];
  total: number;
  page: number;
  limit: number;
}

export const listPaymentRecords = async ({
  jwtToken,
  params,
}: {
  jwtToken: string;
  params?: { page?: number; limit?: number; context_id?: number };
}): Promise<ApiResult<PaymentRecordsRes>> => {
  try {
    const res: AxiosResponse = await api.get("/app-api/customer/payment-records", {
      params,
      headers: { Authorization: `Bearer ${jwtToken}` },
    });
    return { state: "success", data: res.data?.data as PaymentRecordsRes };
  } catch (error) {
    return { state: "error", error };
  }
};

/* ──────────────── 帳戶訂單紀錄（account-history, API1） ──────────────── */

export interface OrderHistoryItem {
  platform_order_id: string;
  display_id: string;
  owner: { context_id: number; label: string };
  supplier: { supplier_id: string; supplier_name: string | null };
  status: string;
  /** 後端 map 好的中文 label */
  status_label: string;
  items_summary: { pn: string; qty: number }[];
  total: number;
  payment_method_label: string | null;
  created_at: string;
  /** 該訂單所屬 supplier 的 system_setting.hide_amount_on_pending；待確認且為 true 時前端隱藏金額。 */
  hide_amount_on_pending?: boolean;
}

export interface AccountHistoryRes {
  total_count: number;
  items: OrderHistoryItem[];
  next_cursor: string | null;
  has_more: boolean;
}

export const listAccountOrderHistory = async ({
  jwtToken,
  xContext,
  params,
}: {
  jwtToken: string;
  /** 由 buildXContextHeader 產生，格式 `context:<id>`（必填，缺 → 後端 400） */
  xContext: string;
  params?: { limit?: number; cursor?: string; status?: string };
}): Promise<ApiResult<AccountHistoryRes>> => {
  try {
    const res: AxiosResponse = await api.get("/app-api/platform-orders/account-history", {
      params,
      headers: { Authorization: `Bearer ${jwtToken}`, "X-Context": xContext },
    });
    return { state: "success", data: res.data?.data as AccountHistoryRes };
  } catch (error) {
    return { state: "error", error };
  }
};

/* ──────────────── 帳戶付款紀錄 + 月結（account-payments, API2） ──────────────── */

export interface AccountPaymentItem {
  platform_order_id: string;
  display_id: string | null;
  context_id: number;
  order_status: string;
  order_status_label: string;
  supplier_id: string;
  supplier_name: string | null;
  amount: number;
  payment_method_label: string | null;
  /** 'cash' | PaymentStatusEnum（captured / failed / ...） */
  payment_status: string;
  paid_at: string | null;
  created_at: string;
  /** 該訂單所屬 supplier 的 system_setting.hide_amount_on_pending；待確認且為 true 時前端隱藏金額。 */
  hide_amount_on_pending?: boolean;
}

export interface MonthlySummary {
  year_month: string;
  total_amount: number;
  transaction_count: number;
}

export interface AccountPaymentsRes {
  monthly_summary: MonthlySummary | null;
  items: AccountPaymentItem[];
  next_cursor: string | null;
  has_more: boolean;
}

export const listAccountPayments = async ({
  jwtToken,
  xContext,
  params,
}: {
  jwtToken: string;
  xContext: string;
  params?: { limit?: number; cursor?: string; year_month?: string };
}): Promise<ApiResult<AccountPaymentsRes>> => {
  try {
    const res: AxiosResponse = await api.get("/app-api/platform-orders/account-payments", {
      params,
      headers: { Authorization: `Bearer ${jwtToken}`, "X-Context": xContext },
    });
    return { state: "success", data: res.data?.data as AccountPaymentsRes };
  } catch (error) {
    return { state: "error", error };
  }
};

/* ──────────────── 共用：訂單狀態 → 中文 label（payment-records 只回 enum） ──────────────── */

export const orderStatusLabelMap: Record<string, string> = {
  pending: "處理中",
  waiting_for_pickup: "等待接單",
  delivering: "正在配送中",
  completed: "訂單已完成",
  canceled: "訂單已取消",
  failed: "處理失敗",
};

export const orderStatusLabel = (status: string): string => orderStatusLabelMap[status] ?? status;

/* ──────────────── 共用：付款方式 → 中文 label（payment-records 回英文 enum） ──────────────── */
export const paymentMethodLabelMap: Record<string, string> = {
  cash_on_delivery: "貨到付款",
  cash: "現金",
  cod: "貨到付款",
  wallet: "錢包支付",
  wallet_balance: "錢包支付",
  store_value: "儲值金",
  line_pay: "LINE Pay",
  credit_card: "信用卡",
  card: "信用卡",
  bank_transfer: "銀行轉帳",
  transfer: "轉帳",
};

/** 付款方式英文 enum → 中文。大小寫／空白／連字號都正規化；無對應時原樣回傳。 */
export const paymentMethodLabel = (method: string | null | undefined): string => {
  if (!method) return "—";
  const key = method.toLowerCase().replace(/[\s-]+/g, "_");
  return paymentMethodLabelMap[key] ?? method;
};
